import React, { useState, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type NumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  allowEmpty?: boolean;
  fallback?: number;
};

const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  integer = false,
  allowEmpty = true,
  fallback,
  inputProps,
  onBlur,
  ...rest
}) => {
  const [text, setText] = useState<string>(value === undefined || Number.isNaN(value) ? '' : String(value));

  useEffect(() => {
    setText((prev) => {
      if (prev === '' && !allowEmpty && value !== undefined && !Number.isNaN(value)) {
        return prev;
      }
      const next = value === undefined || Number.isNaN(value) ? '' : String(value);
      if (prev !== '' && Number(prev) === value) return prev;
      if (prev === '' && (value === undefined || Number.isNaN(value))) return prev;
      return next;
    });
  }, [value, allowEmpty]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);
    if (raw === '') {
      if (allowEmpty) onChange(undefined);
      return;
    }
    const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
    if (Number.isNaN(parsed)) return;
    onChange(parsed);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (text === '') {
      if (!allowEmpty) {
        const fb = fallback ?? min ?? 0;
        setText(String(fb));
        onChange(fb);
      }
    } else {
      let parsed = integer ? parseInt(text, 10) : parseFloat(text);
      if (Number.isNaN(parsed)) {
        if (!allowEmpty) {
          parsed = fallback ?? min ?? 0;
        } else {
          setText('');
          onChange(undefined);
          onBlur?.(e);
          return;
        }
      }
      if (typeof min === 'number' && parsed < min) parsed = min;
      if (typeof max === 'number' && parsed > max) parsed = max;
      if (integer) parsed = Math.round(parsed);
      setText(String(parsed));
      onChange(parsed);
    }
    onBlur?.(e);
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{ min, max, step, ...inputProps }}
    />
  );
};

export default NumberField;
